import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    LinearProgress,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetOrganizationDashboard } from "../../../api/bpm/organizationDashboard";
import { useAuth } from "../../../auth";
import {
    createDefaultDashboardPeriod,
    getDashboardDateRange,
    restoreDashboardPeriod,
} from "../../../components/Dashboard/dashboardPeriod";
import DashboardPeriodSelector from "../../../components/Dashboard/DashboardPeriodSelector";
import OrganizationDashboard from "../../../components/Dashboard/OrganizationDashboard";

const PERIOD_STORAGE_KEY = "smartdocs.organization-dashboard.period";

export const Route = createFileRoute("/__auth/admin/organization-dashboard")({
    component: OrganizationDashboardPage,
    staticData: { title: "routes.organizationDashboard" },
});

export function OrganizationDashboardPage() {
    const { t } = useTranslation();
    const { user, organizations, currentTenant, switchTenant } = useAuth();
    const navigate = Route.useNavigate();
    const [selectedOrganizationKeys, setSelectedOrganizationKeys] = useState<string[]>(
        () => user?.tenant ? [user.tenant] : [],
    );
    const [organizationSelectionReady, setOrganizationSelectionReady] = useState(
        () => Boolean(user?.tenant),
    );
    const [period, setPeriod] = useState(() => restoreDashboardPeriod(
        localStorage.getItem(PERIOD_STORAGE_KEY),
        createDefaultDashboardPeriod(),
    ));
    const dateRange = useMemo(() => getDashboardDateRange(period), [period]);

    useEffect(() => {
        localStorage.setItem(PERIOD_STORAGE_KEY, JSON.stringify(period));
    }, [period]);

    useEffect(() => {
        if (!organizationSelectionReady && user?.tenant) {
            setSelectedOrganizationKeys([user.tenant]);
            setOrganizationSelectionReady(true);
        }
    }, [organizationSelectionReady, user?.tenant]);

    const {
        data,
        isLoading,
        isFetching,
        isError,
        refetch,
        dataUpdatedAt,
    } = useGetOrganizationDashboard(
        {
            startDate: dateRange?.startDate ?? "",
            endDate: dateRange?.endDate ?? "",
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            organizationKeys: selectedOrganizationKeys,
        },
        Boolean(user?.tenant && dateRange && organizationSelectionReady),
    );

    const isInitialLoading = isLoading && !data;
    const isRefreshing = isFetching && Boolean(data);
    const drilldownOrganizationKey = selectedOrganizationKeys.length === 1
        ? selectedOrganizationKeys[0]
        : undefined;

    const openOrganizationProcesses = async (
        organizationKey: string,
        metric: "started" | "completed" | "incidents" = "started",
    ) => {
        if (organizationKey !== currentTenant) {
            await switchTenant(organizationKey);
        }
        const periodSearch = !dateRange
            ? {}
            : metric === "completed"
                ? { endTimeFrom: dateRange.startDate, endTimeTo: dateRange.endDate }
                : metric === "incidents"
                    ? { incidentTimeFrom: dateRange.startDate, incidentTimeTo: dateRange.endDate }
                    : { startTimeFrom: dateRange.startDate, startTimeTo: dateRange.endDate };
        navigate({
            to: "/processes",
            search: {
                page: 1,
                size: 10,
                orderBy: metric === "completed" ? "endTime" : "startTime",
                order: "desc",
                state: metric === "completed"
                    ? "COMPLETED"
                    : metric === "incidents" ? "HAS_INCIDENTS" : undefined,
                ...periodSearch,
                dashboardScope: true,
            },
        });
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ display: "flex", alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 2.5 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>{t("organizationDashboard.title")}</Typography>
                    <Typography color="text.secondary">{t("organizationDashboard.subtitle")}</Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", justifyContent: { xs: "stretch", md: "flex-end" }, minWidth: 0 }}>
                    <Stack direction="row" gap={1.5} alignItems="center" flexShrink={0}>
                        <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                            {dataUpdatedAt
                                ? t("dashboard.processMetrics.lastUpdated", {
                                    date: new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "medium" }).format(dataUpdatedAt),
                                })
                                : t("dashboard.processMetrics.notUpdatedYet")}
                        </Typography>
                        <Button variant="outlined" size="small" startIcon={<RefreshOutlinedIcon />} disabled={isFetching} onClick={() => refetch()}>
                            {t("dashboard.processMetrics.refresh")}
                        </Button>
                    </Stack>
                    <DashboardPeriodSelector value={period} onChange={setPeriod} />
                </Box>
            </Box>

            {isInitialLoading ? (
                <OrganizationDashboardInitialLoading />
            ) : (
                <>
                    {isError ? <Alert severity="error" sx={{ mb: 2.5 }}>{t("organizationDashboard.loadError")}</Alert> : null}
                    <Box aria-busy={isRefreshing} sx={{ position: "relative", minWidth: 0, maxWidth: "100%" }}>
                        {isRefreshing ? <LinearProgress sx={{ position: "sticky", top: 0, zIndex: 2, mb: 1, borderRadius: 1 }} /> : null}
                        <Box sx={{ opacity: isRefreshing ? 0.72 : 1, pointerEvents: isRefreshing ? "none" : "auto", transition: "opacity 160ms ease" }}>
                            <OrganizationDashboard
                                data={data}
                                organizations={organizations}
                                selectedOrganizationKeys={selectedOrganizationKeys}
                                onOrganizationsChange={setSelectedOrganizationKeys}
                                loading={false}
                                error={false}
                                onProcessMetricClick={drilldownOrganizationKey
                                    ? (metric) => openOrganizationProcesses(drilldownOrganizationKey, metric)
                                    : undefined}
                                onOrganizationOpen={(organizationKey, metric = "started") => openOrganizationProcesses(organizationKey, metric)}
                            />
                        </Box>
                    </Box>
                </>
            )}
        </Container>
    );
}

function OrganizationDashboardInitialLoading() {
    const { t } = useTranslation();
    return (
        <Paper
            elevation={0}
            role="status"
            aria-live="polite"
            sx={{
                minHeight: { xs: 360, md: 480 },
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
            }}
        >
            <Stack alignItems="center" gap={2} textAlign="center">
                <CircularProgress size={48} />
                <Box>
                    <Typography variant="h6">{t("dashboard.processMetrics.loadingIndicators")}</Typography>
                    <Typography variant="body2" color="text.secondary">{t("dashboard.processMetrics.loadingIndicatorsDescription")}</Typography>
                </Box>
            </Stack>
        </Paper>
    );
}
